import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface LoginStateData {
  usernameOrEmail?: string;
  password?: string;
  shouldShowModal?: boolean;
  userData?: {
    userId: number;
    email: string;
    username: string;
  };
  expiresAt?: number; // Timestamp for auto-cleanup
  confirmationType?: 'REGISTRY' | 'MFA'; // Track which confirmation type to use in 2FA
}

@Injectable({
  providedIn: 'root',
})
export class MzicLoginStateService {
  private readonly LOGIN_STATE_KEY = 'mzic_login_incomplete_registration_state';
  private readonly serviceId = Math.random().toString(36).substr(2, 9); // Unique ID for this service instance

  private loginStateSource = new BehaviorSubject<LoginStateData>({});
  public loginState$ = this.loginStateSource.asObservable();

  constructor() {
    console.log(
      `[Service ${this.serviceId}] MzicLoginStateService constructor called`,
    ); // Debug log
    this.initStateFromStorage();
  }

  private initStateFromStorage(): void {
    try {
      const savedState = localStorage.getItem(this.LOGIN_STATE_KEY);
      console.log(
        `[Service ${this.serviceId}] InitStateFromStorage - raw localStorage value:`,
        savedState,
      ); // Debug log
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        console.log(
          `[Service ${this.serviceId}] InitStateFromStorage - parsed state:`,
          parsedState,
        ); // Debug log
        this.loginStateSource.next(parsedState);
      }
    } catch (error) {
      console.error(
        `[Service ${this.serviceId}] Error recovering login state from localStorage`,
        error,
      );
      // Don't clear state on initialization error - just log it
      // this.clearState();
    }
  }

  /**
   * Save login form state and modal data for incomplete registration flow
   */
  saveLoginStateForIncompleteRegistration(state: LoginStateData): void {
    // Add expiration timestamp (30 minutes from now)
    const stateWithExpiration = {
      ...state,
      expiresAt: Date.now() + 30 * 60 * 1000, // 30 minutes
    };

    console.log(
      `[Service ${this.serviceId}] Saving login state:`,
      stateWithExpiration,
    ); // Debug log
    this.loginStateSource.next(stateWithExpiration);

    try {
      const serializedState = JSON.stringify(stateWithExpiration);
      localStorage.setItem(this.LOGIN_STATE_KEY, serializedState);

      // Immediately verify the save worked
      const verification = localStorage.getItem(this.LOGIN_STATE_KEY);
      console.log(
        `[Service ${this.serviceId}] State saved to localStorage, verification read:`,
        verification,
      ); // Debug log

      if (!verification) {
        console.error(
          `[Service ${this.serviceId}] FAILED TO SAVE TO LOCALSTORAGE!`,
        );
      }
    } catch (error) {
      console.error(
        `[Service ${this.serviceId}] Error saving to localStorage:`,
        error,
      );
    }
  }

  /**
   * Get current login state
   */
  getLoginState(): LoginStateData {
    // Always read from localStorage to ensure we have the most current state
    try {
      const savedState = localStorage.getItem(this.LOGIN_STATE_KEY);
      if (savedState) {
        const parsedState = JSON.parse(savedState);

        // Check if state has expired
        if (parsedState.expiresAt && Date.now() > parsedState.expiresAt) {
          console.log(`[Service ${this.serviceId}] State expired, clearing...`);
          this.clearState();
          return {};
        }

        console.log(
          `[Service ${this.serviceId}] Getting login state from localStorage:`,
          parsedState,
        ); // Debug log
        return parsedState;
      }
    } catch (error) {
      console.error(
        `[Service ${this.serviceId}] Error reading login state from localStorage`,
        error,
      );
    }

    // Fallback to BehaviorSubject value
    const state = this.loginStateSource.value;
    console.log(
      `[Service ${this.serviceId}] Getting login state from BehaviorSubject (fallback):`,
      state,
    ); // Debug log
    return state;
  }

  /**
   * Check if there's persisted login state for incomplete registration
   */
  hasPersistedLoginState(): boolean {
    // Check localStorage directly first
    try {
      const savedState = localStorage.getItem(this.LOGIN_STATE_KEY);
      if (savedState) {
        const parsedState = JSON.parse(savedState);

        // Check if state has expired
        if (parsedState.expiresAt && Date.now() > parsedState.expiresAt) {
          console.log(`[Service ${this.serviceId}] State expired, clearing...`);
          this.clearState();
          return false;
        }

        const hasState = !!(
          parsedState.usernameOrEmail || parsedState.shouldShowModal
        );
        console.log(
          `[Service ${this.serviceId}] HasPersistedLoginState - localStorage check:`,
          hasState,
          parsedState,
        ); // Debug log
        return hasState;
      }
    } catch (error) {
      console.error(
        `[Service ${this.serviceId}] Error checking persisted login state`,
        error,
      );
    }

    // Fallback to getLoginState method
    const state = this.getLoginState();
    const hasState = !!(state.usernameOrEmail || state.shouldShowModal);
    console.log(
      `[Service ${this.serviceId}] HasPersistedLoginState - fallback check:`,
      hasState,
      state,
    ); // Debug log
    return hasState;
  }

  /**
   * Clear the login state
   */
  clearState(): void {
    console.log(`[Service ${this.serviceId}] Clearing login state`); // Debug log
    this.loginStateSource.next({});
    localStorage.removeItem(this.LOGIN_STATE_KEY);

    // Verify clear worked
    const verification = localStorage.getItem(this.LOGIN_STATE_KEY);
    console.log(
      `[Service ${this.serviceId}] State cleared, verification read:`,
      verification,
    ); // Debug log
  }

  /**
   * Update specific parts of the login state
   */
  updateLoginState(partialState: Partial<LoginStateData>): void {
    const currentState = this.getLoginState();
    const updatedState = { ...currentState, ...partialState };
    this.saveLoginStateForIncompleteRegistration(updatedState);
  }
}
